/**
 * Error hierarchy.
 *
 * Failures are separated by whether the loop can recover:
 *
 * - `RecoverableAgentError` — the pipeline degrades to `unknown`/`away` and retries (camera
 *   unplugged, a dropped frame, a provider hiccup). §27 requires graceful recovery from
 *   camera failure, so these must never terminate monitoring.
 * - Everything else is fatal at startup: missing consent, invalid config, absent model.
 *   Failing loudly is correct — a monitoring agent silently running with a broken policy is
 *   worse than one that refuses to start.
 */

export class AgentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = new.target.name;
  }
}

/** Configuration is missing, malformed or internally inconsistent. */
export class ConfigError extends AgentError {
  readonly problems: readonly string[];

  constructor(message: string, problems: readonly string[] = []) {
    super(problems.length > 0 ? `${message}\n  - ${problems.join('\n  - ')}` : message);
    this.problems = problems;
  }
}

/**
 * Monitoring was requested without a recorded, current consent acknowledgement.
 *
 * Thrown before the camera is ever opened — the code-level expression of the "no covert
 * monitoring" requirement (§3, §25).
 */
export class ConsentError extends AgentError {}

/** The camera could not be opened or configured. */
export class CameraError extends AgentError {}

/** A required CV model asset or runtime is not available. */
export class ModelUnavailableError extends AgentError {}

/** Calibration could not be completed, or a stored profile is unusable. */
export class CalibrationError extends AgentError {}

/** A transient failure the run loop should absorb and retry. */
export class RecoverableAgentError extends AgentError {}

export class FrameReadError extends RecoverableAgentError {}

export class SinkWriteError extends RecoverableAgentError {}
